import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { doc, getDoc, writeBatch, query, collection, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { ChessSocialContext } from "../../util/ChessSocialContext";
import chessslogo from "../../images/chessslogo.png";
import { usePremium } from '../../util/PremiumFilter';

// Import shared hooks
import { useProfileData } from '../../hooks/useProfileData';
import { useFollowSystem } from '../../hooks/useFollowSystem';
import { useUserGames } from '../../hooks/useUserGames';
import GameSummaryMobile from '../components/GameSummaryMobile';
import EditProfileMobile from '../components/EditProfileMobile';
import { isNativeApp } from '../../util/platformDetection';
import { cleanProfanity } from '../../util/filterProfanity';

const MobileProfile = (props) => {
  // Mobile-specific state
  const [activeTab, setActiveTab] = useState('ALL GAMES');
  const [isMobileView, setIsMobileView] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { renderUserWithBadge, isPremiumUser } = usePremium();
  
  // Determine if viewing own profile
  const editProfile = location.pathname === "/profile" || (location.pathname.startsWith("/profile/") && 
    props?.user?.credentials?.handle === props?.match?.params?.handle);
  
  const user = editProfile ? props?.user?.credentials?.handle : props?.match?.params?.handle;
  const authenticatedUserHandle = props?.user?.credentials?.handle;
  const authenticatedUserId = props?.user?.credentials?.userId;

  // Use profile data hooks
  const {
    data,
    userData,
    followersCount,
    followingCount,
    followersList,
    followingList,
    targetUserId,
    isFollowing,
    loading: profileLoading,
    error: profileError,
    refreshProfileData,
    setFollowersCount,
    setFollowingCount,
    setIsFollowing
  } = useProfileData(user, authenticatedUserHandle, authenticatedUserId, editProfile);

  // Use follow system hook
  const {
    isFollowLoading,
    handleFollowClick,
    unfollowUser
  } = useFollowSystem(authenticatedUserHandle, authenticatedUserId, isPremiumUser);

  // Use games data hook
  const {
    games,
    folders,
    nonEmptyFolders,
    currentFolderGames,
    isLoadingGames,
    postsCount,
    handleTabChange
  } = useUserGames(user, authenticatedUserId, editProfile);

  // Handle follow button click
  const onFollowClick = () => {
    handleFollowClick(isFollowing, user, targetUserId, (newFollowStatus, followersChange) => {
      setIsFollowing(newFollowStatus);
      setFollowersCount(prev => Math.max(0, prev + followersChange));
      refreshProfileData();
    });
  };

  // Function to remove a follower from current user's followers list
  const removeFollower = useCallback(async (followerHandle) => {
    try {
      if (!authenticatedUserHandle || !authenticatedUserId) {
        alert("You must be logged in to remove followers");
        return false;
      }

      // Start a batch write
      const batch = writeBatch(firestore);

      // Get the current user's document (using handle as document ID)
      const currentUserRef = doc(firestore, "users", authenticatedUserHandle);
      const currentUserSnap = await getDoc(currentUserRef);

      // Find the follower's user document by querying for their handle
      const followerQuery = query(
        collection(firestore, "users"),
        where("handle", "==", followerHandle)
      );

      const followerSnapshot = await getDocs(followerQuery);

      if (followerSnapshot.empty) {
        console.error("Follower not found with handle:", followerHandle);
        alert("Could not find the follower in the database.");
        return false;
      }

      const followerDoc = followerSnapshot.docs[0];
      const followerData = followerDoc.data();

      if (currentUserSnap.exists()) {
        const currentUserData = currentUserSnap.data();

        // Remove follower from current user's followers list
        const normalizedFollowerHandle = followerHandle.toLowerCase();
        let followers = currentUserData.followers || [];
        followers = followers.filter(handle => handle.toLowerCase() !== normalizedFollowerHandle);

        // Update current user's document
        batch.update(currentUserRef, { followers });

        // Remove current user from follower's following list
        const normalizedCurrentHandle = authenticatedUserHandle.toLowerCase();
        let following = followerData.following || [];
        following = following.filter(handle => handle.toLowerCase() !== normalizedCurrentHandle);

        // Update follower's document using their document ID
        batch.update(doc(firestore, "users", followerDoc.id), { following });

        // Commit the batch
        await batch.commit();

        // Update local state
        setFollowersCount(prev => Math.max(0, prev - 1));
        refreshProfileData();

        return true;
      } else {
        console.error("Current user document not found");
        return false;
      }
    } catch (error) {
      console.error("Error removing follower:", error);
      alert("Failed to remove follower. Please try again.");
      return false;
    }
  }, [authenticatedUserHandle, authenticatedUserId, setFollowersCount, refreshProfileData]);

  // Function to unfollow a user from current user's following list
  const handleUnfollow = useCallback(async (followingHandle) => {
    try {
      // Find the target user's ID
      const followingRef = doc(firestore, "users", followingHandle);
      const followingSnap = await getDoc(followingRef);
      
      if (!followingSnap.exists()) {
        alert("User not found");
        return false;
      }

      const followingUserId = followingSnap.id;
      const success = await unfollowUser(followingHandle, followingUserId);
      
      if (success) {
        setFollowingCount(prev => Math.max(0, prev - 1));
        refreshProfileData();
      }

      return success;
    } catch (error) {
      console.error("Error unfollowing user:", error);
      alert("Failed to unfollow user. Please try again.");
      return false;
    }
  }, [unfollowUser, setFollowingCount, refreshProfileData]);

  // Get navigation context from location state
  const getNavigationContext = () => {
    const state = location.state;
    if (!state || !state.returnTo) return null;
    
    return {
      returnTo: state.returnTo,
      returnLabel: state.returnLabel || 'Back',
      from: state.from,
      navigationStack: state.navigationStack || []
    };
  };

  // Build navigation context for next profile
  const buildNextNavigationContext = (targetProfile, label) => {
    const currentContext = getNavigationContext();
    
    const currentEntry = {
      returnTo: user,
      returnLabel: `Back to ${editProfile ? 'Your Profile' : `@${user}`}`,
      from: 'profile'
    };

    let navigationStack = [];
    
    if (currentContext && currentContext.navigationStack) {
      // Preserve existing stack and add current profile
      navigationStack = [...currentContext.navigationStack, currentEntry];
    } else if (currentContext) {
      // First level of navigation - create stack with previous and current
      navigationStack = [currentContext, currentEntry];
    } else {
      // Direct navigation to target profile - only add current
      navigationStack = [currentEntry];
    }

    const result = {
      returnTo: user, // This should be the CURRENT profile (where we're coming FROM)
      returnLabel: label,
      from: 'profile',
      navigationStack: navigationStack
    };
    
    return result;
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    // Use browser's native back navigation
    // This will properly navigate through the browser history stack
    // including preserving scroll position and state
    history.goBack();
  };

  // Set initial active tab based on available data
  useEffect(() => {
    if (!isLoadingGames && activeTab === 'ALL GAMES') {
      if (games.length === 0 && nonEmptyFolders.length > 0) {
        setActiveTab(nonEmptyFolders[0].folderName);
      }
    }
  }, [isLoadingGames, games.length, nonEmptyFolders, activeTab]);

  // Render conditional back header
  const renderBackHeader = () => {
    const context = getNavigationContext();
    if (!context) return null;

    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '12px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flexShrink: 0
      }}>
        <button
          onClick={handleBackNavigation}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#888',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className="fa-solid fa-arrow-left" style={{ fontSize: '16px' }}></i>
          <span>{context.returnLabel}</span>
        </button>
      </div>
    );
  };

  // Mobile-optimized profile header
  const renderProfileHeader = () => {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '20px'
      }}>
        {/* Profile Picture and Basic Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '15px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '2px solid gold',
            backgroundColor: '#121212',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={data && data.photoUrl ? data.photoUrl : chessslogo}
              alt={`${user}'s profile`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{
              color: 'gold',
              fontSize: '18px',
              fontWeight: 'bold',
              margin: '0 0 5px 0'
            }}>
              {userData ? renderUserWithBadge(user, userData.premium, false) : renderUserWithBadge(user, false)}
            </h2>
            <div style={{
              color: '#888',
              fontSize: '14px'
            }}>
              {data.status || ''}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {editProfile ? (
              <button
                onClick={() => setShowEditModal(true)}
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid gold',
                  color: 'gold',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Edit
              </button>
            ) : (
              <button
                onClick={onFollowClick}
                disabled={isFollowLoading}
                style={{
                  backgroundColor: isFollowing ? '#333' : 'gold',
                  border: isFollowing ? '1px solid #666' : 'none',
                  color: isFollowing ? '#888' : 'black',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: isFollowLoading ? 'default' : 'pointer',
                  fontWeight: 'bold',
                  opacity: isFollowLoading ? 0.7 : 1
                }}
              >
                {isFollowLoading ? '...' : isFollowing ? 'Following' : 'Follow'}
              </button>
            )}
            
            {/* ABOUT button - show if user has bio or social links */}
            {data && (data.biography || data.youtubeLink || data.tiktokLink || data.instagramLink || data.twitterLink) && (
              <button
                onClick={() => setShowAboutModal(true)}
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid #666',
                  color: '#ccc',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                About
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: '10px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
              {profileLoading ? '...' : postsCount}
            </div>
            <div style={{ color: '#888', fontSize: '12px' }}>Posts</div>
          </div>
          <div 
            style={{ textAlign: 'center', cursor: 'pointer' }}
            onClick={() => setShowFollowersModal(true)}
          >
            <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
              {profileLoading ? '...' : followersCount}
            </div>
            <div style={{ color: '#888', fontSize: '12px' }}>Followers</div>
          </div>
          <div 
            style={{ textAlign: 'center', cursor: 'pointer' }}
            onClick={() => setShowFollowingModal(true)}
          >
            <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
              {profileLoading ? '...' : followingCount}
            </div>
            <div style={{ color: '#888', fontSize: '12px' }}>Following</div>
          </div>
          {/* Bookmark button - only for own profile */}
          {editProfile && (
            <div 
              style={{ textAlign: 'center', cursor: 'pointer' }}
              onClick={() => history.push('/bookmarks')}
            >
              <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                <i className="fa-solid fa-bookmark"></i>
              </div>
              <div style={{ color: '#888', fontSize: '12px' }}>Bookmarks</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Mobile-optimized tab navigation
  const renderTabNavigation = () => {
    // Build tabs array - ALL GAMES + public folders with games
    const tabs = [];
    
    // Always show ALL GAMES tab
    tabs.push({ id: 'ALL GAMES', label: 'ALL GAMES' });
    
    // Add public folders that have games
    nonEmptyFolders.forEach(folder => {
      tabs.push({
        id: folder.folderName,
        label: folder.folderName.toUpperCase()
      });
    });

    // If no tabs available, show placeholder
    if (tabs.length === 0) {
      return null;
    }

    return (
      <div style={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333'
      }}>
        <div style={{
          display: 'flex',
          minWidth: 'fit-content'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                handleTabChange(tab.id);
              }}
              style={{
                minWidth: '100px',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? '#333' : 'transparent',
                color: activeTab === tab.id ? 'gold' : '#888',
                fontSize: '11px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
            >
              {cleanProfanity(tab.label)}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Mobile content area
  const renderTabContent = () => {
    if (isLoadingGames) {
      return (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
          </div>
        </div>
      );
    }

    // Determine which games to show
    const gamesToShow = activeTab === 'ALL GAMES' ? games : currentFolderGames;

    if (gamesToShow.length === 0) {
      return (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          textAlign: 'center',
          padding: '40px 20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px', color: '#666' }}>
            {activeTab === 'ALL GAMES' ? (
              <i className="fa-solid fa-chess-pawn"></i>
            ) : (
              <i className="fa-solid fa-folder"></i>
            )}
          </div>
          <h3 style={{ color: '#888', marginBottom: '10px' }}>
            {activeTab === 'ALL GAMES' ? 'No Games Yet' : `No Games in ${cleanProfanity(activeTab)}`}
          </h3>
          <button
            onClick={() => history.push('/Analysis')}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#666',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '0'
            }}
          >
            Start Creating
          </button>
        </div>
      );
    }

    return (
      <div style={{
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '15px'
      }}>
        {/* Games Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '15px',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)'
        }}>
          {gamesToShow.map(game => (
            <div key={game.id} style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #333'
            }}>
              <GameSummaryMobile
                gameData={game}
                boardWidth={(window.innerWidth - 60) / Math.floor((window.innerWidth - 30) / 165) + 4} // Dynamic width based on available columns, plus 4px
                showDetails={true}
                onClick={() => {
                  // Get all games from current tab for navigation
                  const gameIds = (activeTab === 'ALL GAMES' ? games : currentFolderGames).map(g => g.id);
                  
                  history.push(`/games/${game.id}`, { 
                    origin: 'PROFILE',
                    gameIds: gameIds,
                    returnLabel: `Back to @${user}'s Profile`
                  });
                }}
              />
            </div>
          ))}
        </div>

      </div>
    );
  };

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
      backgroundColor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      paddingTop: 'env(safe-area-inset-top)', // Header only in native app
      paddingBottom: 'env(safe-area-inset-bottom)' // iPhone gesture area
    }}>
      {renderBackHeader()}
      {renderProfileHeader()}
      {renderTabNavigation()}
      {renderTabContent()}

      {/* Followers Modal */}
      {showFollowersModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '15vh' // Moved down to avoid iPhone notch cutoff
          }}
          onClick={() => setShowFollowersModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '350px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: editProfile ? 'space-between' : 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{ color: 'gold', margin: 0 }}>Followers ({followersCount})</h3>
              {editProfile && (
                <button
                  onClick={() => setShowFollowersModal(false)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ff0050',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '5px'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            {followersList && followersList.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {followersList.map(follower => (
                  <div key={follower} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: 'white',
                    padding: '8px 12px',
                    backgroundColor: '#333',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    <span 
                      onClick={() => {
                        setShowFollowersModal(false);
                        const navigationContext = buildNextNavigationContext(
                          follower,
                          `Back to ${editProfile ? 'Your Profile' : `@${user}`}`
                        );
                        history.push(`/profile/${follower}`, navigationContext);
                      }}
                      style={{ cursor: 'pointer', hover: { textDecoration: 'underline' } }}
                    >
                      @{follower}
                    </span>
                    {editProfile && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFollower(follower);
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#ff6b6b',
                          fontSize: '14px',
                          cursor: 'pointer',
                          padding: '2px'
                        }}
                        title="Remove follower"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                No followers yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* Following Modal */}
      {showFollowingModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '15vh' // Moved down to avoid iPhone notch cutoff
          }}
          onClick={() => setShowFollowingModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '350px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: editProfile ? 'space-between' : 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{ color: 'gold', margin: 0 }}>Following ({followingCount})</h3>
              {editProfile && (
                <button
                  onClick={() => setShowFollowingModal(false)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ff0050',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '5px'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            {followingList && followingList.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {followingList.map(following => (
                  <div key={following} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: 'white',
                    padding: '8px 12px',
                    backgroundColor: '#333',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    <span 
                      onClick={() => {
                        setShowFollowingModal(false);
                        const navigationContext = buildNextNavigationContext(
                          following,
                          `Back to ${editProfile ? 'Your Profile' : `@${user}`}`
                        );
                        history.push(`/profile/${following}`, navigationContext);
                      }}
                      style={{ cursor: 'pointer', hover: { textDecoration: 'underline' } }}
                    >
                      @{following}
                    </span>
                    {editProfile && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnfollow(following);
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#ff6b6b',
                          fontSize: '14px',
                          cursor: 'pointer',
                          padding: '2px'
                        }}
                        title="Unfollow"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                Not following anyone yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* ABOUT Modal - Bio and Social Links */}
      {showAboutModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '15vh' // Moved down to avoid iPhone notch cutoff
          }}
          onClick={() => setShowAboutModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '350px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: 'gold', margin: 0 }}>About {user}</h3>
              <button
                onClick={() => setShowAboutModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#ff0050',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Biography */}
            {data.biography && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px' }}>Biography</h4>
                <p style={{
                  color: 'white',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}>
                  {data.biography}
                </p>
              </div>
            )}

            {/* Social Links */}
            {(data.youtubeLink || data.tiktokLink || data.instagramLink || data.twitterLink) && (
              <div>
                <h4 style={{ color: '#ccc', fontSize: '14px', marginBottom: '12px' }}>Social Links</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {data.youtubeLink && (
                    <a
                      href={data.youtubeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        backgroundColor: '#ff0000',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fa-brands fa-youtube" style={{ fontSize: '16px' }}></i>
                      YouTube
                    </a>
                  )}
                  {data.tiktokLink && (
                    <a
                      href={data.tiktokLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        backgroundColor: '#000000',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fa-brands fa-tiktok" style={{ fontSize: '16px' }}></i>
                      TikTok
                    </a>
                  )}
                  {data.instagramLink && (
                    <a
                      href={data.instagramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fa-brands fa-instagram" style={{ fontSize: '16px' }}></i>
                      Instagram
                    </a>
                  )}
                  {data.twitterLink && (
                    <a
                      href={data.twitterLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        backgroundColor: '#1da1f2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fa-brands fa-x-twitter" style={{ fontSize: '16px' }}></i>
                      Twitter
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileMobile
          onClose={() => setShowEditModal(false)}
          onSave={() => {
            // Refresh profile data after save
            refreshProfileData();
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  data: state.data
});

export default connect(mapStateToProps)(MobileProfile);